const CHUNK_ERROR_RE =
  /Loading chunk \d+ failed|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i;
const RELOAD_KEY = 'elevate-deploy-reload';

function shouldReloadForChunk(error) {
  const message = error?.message || String(error || '');
  const name = error?.name || '';
  return CHUNK_ERROR_RE.test(message) || name === 'ChunkLoadError';
}

function reloadOnce(clearReloadGuard = false) {
  if (clearReloadGuard) {
    sessionStorage.removeItem(RELOAD_KEY);
  } else if (sessionStorage.getItem(RELOAD_KEY)) {
    return;
  } else {
    sessionStorage.setItem(RELOAD_KEY, '1');
  }

  window.location.reload();
}

function getManifestFingerprint(manifest) {
  if (!manifest?.files) {
    return '';
  }

  return manifest.files['main.js'] || Object.values(manifest.files).join('|');
}

/**
 * After a deploy, old tabs keep hashed chunk URLs that no longer exist → white screen.
 * This:
 * 1) Reloads once on ChunkLoadError / failed dynamic import
 * 2) Polls /asset-manifest.json and reloads when the build fingerprint changes
 */
export function initDeployRefresh(options = {}) {
  const { manifestUrl = '/asset-manifest.json', pollIntervalMs = 60000 } = options;

  window.addEventListener('unhandledrejection', (event) => {
    if (shouldReloadForChunk(event.reason)) {
      event.preventDefault?.();
      reloadOnce();
    }
  });

  window.addEventListener('error', (event) => {
    if (shouldReloadForChunk(event.error || { message: event.message })) {
      reloadOnce();
    }
  });

  let baseline = null;

  async function checkManifest() {
    try {
      const response = await fetch(`${manifestUrl}?_=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        return;
      }

      const manifest = await response.json();
      const fingerprint = getManifestFingerprint(manifest);

      if (!fingerprint) {
        return;
      }

      if (baseline === null) {
        baseline = fingerprint;
        return;
      }

      if (fingerprint !== baseline) {
        reloadOnce(true);
      }
    } catch {
      // Ignore transient network errors during polling.
    }
  }

  checkManifest();
  window.setInterval(checkManifest, pollIntervalMs);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkManifest();
    }
  });
}
